import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styles from './GroupUsersTable.module.css';
import dayjs from 'dayjs';
import { TableStateType } from '../../../utils/contextapi/TableProvider';
import { InnocodeButton, InnocodeDatatable } from '@intsoft/innocode-core';


const GroupUsersTable = ({groupId, datas, onAddUser, onDeleteUsers, context, onPrevPage, onNextPage, disablePrev,
   disableNext, pageNumber, totalCount, listSize, sortField, sortOrder, onSortChange, isMobileWindow }) => {
  const [selectedRows, setSelectedRows] = useState([]);

  // 삭제 핸들러
  const handleDeleteSelected = useCallback(() => {
    if (selectedRows.length > 0) {
      onDeleteUsers(selectedRows.map(row => row.profileId));
      setSelectedRows([]);
    }
  }, [selectedRows, onDeleteUsers]);

  // 페이지 변경 핸들러
  const handlePageChange = useCallback((newPage) => {
    if (newPage > pageNumber) {
      onNextPage();
    } else {
      onPrevPage();
    }
  }, [pageNumber, onNextPage, onPrevPage]);

  // 컬럼 정의
  const columns = useMemo(() => {
    const baseColumns = [
      {
        id: 'userId',
        header: '아이디',
        accessorKey: 'userId',
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img
              src={row.imgUrl || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
              alt="프로필 이미지"
              style={{ width: "28px", height: "28px", borderRadius: "50%" }}
            />
            <span>{`${row.name} (${row.userId})`}</span>
          </div>
        ),
      },
    ];

    // 모바일이 아닐 때만 가입일 컬럼 추가
    if (!isMobileWindow) {
      baseColumns.push({
        id: 'createdDate',
        header: '가입일',
        accessorKey: 'createdDate',
        enableSorting: true,
        cell: ({ row }) => (
          <span>{row.createdDate ? dayjs(Number(row.createdDate)).format('YYYY-MM-DD') : '-'}</span>
        ),
      });
    }

    // 모바일이 아닐 때만 마지막 접속일 컬럼 추가
    if (!isMobileWindow) {
      baseColumns.push({
        id: 'lastConnectionDate',
        header: '마지막 접속일',
        accessorKey: 'lastConnectionDate',
        enableSorting: false,
        cell: ({ row }) => (
          <span>{row.lastConnectionDate ? dayjs(Number(row.lastConnectionDate)).format('YYYY-MM-DD HH:mm:ss') : '-'}</span>
        ),
      });
    }

    baseColumns.push({
      id: 'auth',
      header: '권한',
      accessorKey: 'auth',
      enableSorting: false,
      cell: ({ row }) => (
        <span>{Array.isArray(row.auth) ? row.auth.join(", ") : row.auth}</span>
      ),
    });

    // 모바일이 아닐 때, 그룹이 "0"이 아닐 때만 제거 컬럼 추가
    if (!isMobileWindow && groupId !== "0") {
      baseColumns.push({
        id: 'remove',
        header: '그룹 제거',
        accessorKey: 'remove',
        enableSorting: false,
        align: 'center',
        cell: ({ row }) => (
          <InnocodeButton 
            backgroundColor='var(--status-negative, #f83a3a)'
            onClick={(e) => {
              e.stopPropagation();
              onDeleteUsers([row.profileId]);
            }}
          >
            제거
          </InnocodeButton>
        ),
      });
    }

    return baseColumns;
  }, [isMobileWindow, groupId, onDeleteUsers]);

  return (
    <div className={styles.tableSection}>
      <InnocodeDatatable
        className={styles.dataTable}
        data={datas}
        columns={columns}
        enableCustomHeader={true}
        customHeader={
          <div className={styles.customHeaderContainer}>
            <div className={styles.customHeaderSearchContainer}>
              {/* 검색 기능이 필요하면 여기에 추가 */}
            </div>
            <div className={styles.customHeaderActionContainer}>
              {groupId !== "0" && (
                <>
                  <InnocodeButton
                    size="md"
                    backgroundColor='var(--status-negative, #f83a3a)'
                    onClick={handleDeleteSelected}
                    disabled={selectedRows.length === 0}
                  >
                    제거
                  </InnocodeButton>
                  <InnocodeButton
                    size="md"
                    theme="line"
                    designType="primary"
                    onClick={onAddUser}
                  >
                    유저 추가
                  </InnocodeButton>
                </>
              )}
            </div>
          </div>
        }
        enableTableHeader={true}
        enableSorting={true}
        enableCheckbox={groupId !== "0"}
        selectedRows={selectedRows}
        onSelectedRowsChange={setSelectedRows}
        enablePagination={true}
        manualPagination={true}
        pageSize={listSize}
        totalCount={totalCount}
        currentPage={pageNumber}
        onPageChange={handlePageChange}
        emptyMessage="유저가 없습니다."
        rowClassName={styles.tableRow}
      />
    </div>
  );
};

export default GroupUsersTable;
